import React, {useMemo} from 'react';
import {Badge, Checkbox, Empty, Table, Switch} from "antd";
import {Link} from "react-router-dom";

import LoadingSpinner from '../../../components/LoadingSpinner';
import UserImageCircle from '../../../components/UserImageCircle';
import InfoTooltip from '../../../components/InfoTooltip';
import {useIsMutating, useMutation, useQueryClient} from "@tanstack/react-query";
import User, {talentUpdateRequest} from "../../../models/User";
import {useAuth} from "../../../providers/AuthProvider";
import {useAPI} from "../../../config/API";

const MembersPanel = (props) => {

    const {auth, update: updateAuth} = useAuth();
    const queryClient = useQueryClient();
    const isMutatingTalent = useIsMutating(['talentUpdate'])

    const updateClientOrg = (data) => {
        return updateAuth({
            ...auth,
            client_organization: {
                ...auth.clientOrg(),
                total_supplied_talent_users: data.length,
                total_supplied_matching_talent_users: TB.filter(data, {'is_available': true, offers_byo_work: true})?.length,
                total_supplied_team_talent_users: TB.filter(data, {'is_available': true, offers_service_work: true})?.length
            }
        })
    }
    const {
        data,
        isFetching,
        isInitialLoading: isLoading,
        error,
        refetch
    } = useAPI(['fetchAllSupplierTalentResources', auth.clientOrg?.()?.id],  {
        onSuccess: async (data) => {
            console.log('onAPIIIIISuccess', )
            updateClientOrg(data)
        },

        enabled: !!auth && !!auth.clientOrg?.()?.id && auth.canManageClientResourceTalent()
    });

    const members = useMemo(() => {
        if (!data) return undefined;

        let records = TB.map(data || [], item => User.make(({...item, key: `member-${item.id}`})))
        records = TB.filter(records, user => !user.isDeactivated())
        return TB.orderBy(records, ['added_at'], 'desc')
    }, [data, isLoading]);


    const updateTalentMutation = useMutation(['talentUpdate'], ({talentId, params}) => {
            return talentUpdateRequest(params, `/api/clients/${auth.supplierOrg()?.id}/users/${talentId}/update`)
        },{
            onSuccess: (user) => {
                if (user) {
                    queryClient.setQueryData(['fetchAllSupplierTalentResources', auth.clientOrg?.()?.id], prevData => {
                        let recordIndex = TB.findIndex(prevData, {id:  user.id});

                        let updatedData = TB.set([...prevData], recordIndex, user);
                        updateClientOrg(updatedData);
                        return updatedData;
                    });
                    queryClient.invalidateQueries('fetchSupplierDashboard')

                    localStorage.removeItem('jobBoardParams')
                    return TB.toast('Talent Resource Availability Updated')
                }

            },
            onError: (error) => {
                return TB.showServerErrorToast();
            }
        }
    )

    const talentAccountActionMutation = useMutation(['toggleTalentDeactivate'], ({talent, talentMethod}) => {
            return talent[talentMethod]?.();
        }, {
            onSuccess: (user) => {
                if (user) {
                    queryClient.setQueryData(['fetchAllSupplierTalentResources', auth.clientOrg?.()?.id], prevData => {
                        let recordIndex = TB.findIndex(prevData, {id: user.id});

                        let updatedData = TB.set([...prevData], recordIndex, user);
                        updateClientOrg(updatedData);
                        return updatedData;
                    });
                    queryClient.invalidateQueries('fetchSupplierDashboard')

                    localStorage.removeItem('jobBoardParams')
                    return TB.toast('Talent Resource Account ' + user.isDeactivated() ? 'Deactivated' : ' Restored')
                }
            },
            onError: (error) => {
                return TB.showServerErrorToast();
            }
        }
    )
    const actionsDisabled = talentAccountActionMutation.isLoading || updateTalentMutation.isLoading || isMutatingTalent > 0;

    const columns = [
        {
            title: '',
            dataIndex: 'image_path',
            key: 'image',
            render: (text, record) => <UserImageCircle className="img-grayscale" user={record} size={50} />,
        },
        {
            title: 'Name',
            dataIndex: 'first_name',
            key: 'name',
            render: (text, record) => (
                <div className="flex flex-col items-left max-w-[200px] overflow-hidden relative">
                    <p className="text-sm text-info font-semibold text-ellipsis whitespace-nowrap overflow-hidden">{record.firstNamePlusInitial?.()}</p>
                    {record.job_title && (<p className="text-xs text-ellipsis whitespace-nowrap overflow-hidden">{record.job_title}</p>)}
                    <a className="text-xs text-info hover:font-medium hover:underline text-ellipsis whitespace-nowrap overflow-hidden" href={`mailto:${record.email}`}
                       target="_blank">{record.email}</a>
                </div>
            ),
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (text, record) => {
                let label = User.labelForStatus(record.status);

                let badgeStatus;
                switch (label) {
                    case 'Pending Registration':
                        badgeStatus = 'processing';
                        break;
                    case 'Registered':
                        badgeStatus = 'success';
                        break;
                    case 'Inactive':
                        badgeStatus = 'error';
                        break;
                }
                return (
                    <div className="flex flex-row items-center gap-2 flex-nowrap">
                        <Badge status={badgeStatus} />
                        <span className="text-xs">
                            {label}
                        </span>
                    </div>
                )
            },
        },
        {
            title: 'Availability',
            key: 'is_available',
            render: (_, record) => (
                <div className="flex justify-between items-center talent-member-card">
                    <div className="flex flex-col items-start">
                        <Checkbox
                            className="m-0"
                            checked={record.offers_byo_work}
                            disabled={actionsDisabled || updateTalentMutation.isLoading || !record.is_active || !record.is_available}
                            onChange={(checked, event) => {
                                if (actionsDisabled) {
                                    return
                                }

                                return updateTalentMutation.mutate({talentId: record.id, params:{
                                    offers_byo_work: !record.offers_byo_work
                                }})
                            }}
                        >
                            <p className="text-xxs whitespace-no-wrap">
                                Available for hire by role
                            </p>
                        </Checkbox>
                        <Checkbox
                            className="m-0"
                            checked={record.offers_service_work}
                            disabled={actionsDisabled || updateTalentMutation.isLoading || !record.is_active || !record.is_available}
                            onChange={(checked, event) => {
                                if (actionsDisabled) {
                                    return
                                }

                                return updateTalentMutation.mutate({talentId: record.id, params:{
                                    offers_service_work: !record.offers_service_work
                                }})
                            }}
                        >
                            <p className="text-xxs whitespace-no-wrap">
                                Available for hire by team
                            </p>
                        </Checkbox>
                    </div>

                    <div>
                        <Switch
                            style={{minWidth: 98}}
                            checkedChildren="Available"
                            unCheckedChildren="Unavailable"
                            disabled={actionsDisabled || !record.is_active}
                            checked={record.is_active && record.is_available}
                            onChange={(checked, event) => {
                                if (actionsDisabled) {
                                    return
                                }
                                return updateTalentMutation.mutate({talentId: record.id, params:{
                                    is_available: !record.is_available
                                }})
                            }}
                            loading={updateTalentMutation.isLoading}
                        />
                    </div>
                </div>
            ),
            shouldCellUpdate: (record, prevRecord) => {
                return record.is_available !== prevRecord.is_available ||
                    record.offers_byo_work !== prevRecord.offers_byo_work ||
                    record.offers_service_work !== prevRecord.offers_service_work;
            }
        },
        {
            title: 'Action',
            key: 'actions',
            render: (_, record) => (
                <div className="flex gap-2.5 justify-end">
                    <div>
                        <Link
                            to={`/supplier/talent-resources/${record.id}`}
                            id={`edit-member-btn-${record.id}`}
                            className="btn btn-primary table-btn border-none"
                            disabled={isLoading || updateTalentMutation.isLoading || talentAccountActionMutation.isLoading}
                        >
                            <i className="fa fa-edit" />
                        </Link>
                        <InfoTooltip type="primary" target={`edit-member-btn-${record.id}`}>
                            Edit Member Profile
                        </InfoTooltip>
                    </div>
                    <div>
                        <button
                            className="btn bg-danger text-white table-btn"
                            id={`delete-member-btn-${record.id}`}
                            onClick={async () => {
                                if (!record.isDeactivated()) {
                                    let didConfirm = await TB.sysAlert('client.confirm_deactivate_user', {
                                        name: record.fullName()
                                    })

                                    if (!didConfirm) {
                                        return;
                                    }
                                }
                                return talentAccountActionMutation.mutate({talent: record, talentMethod: record.isDeactivated() ? 'restore' : 'deactivate'})
                            }}
                            disabled={isLoading || updateTalentMutation.isLoading || talentAccountActionMutation.isLoading}
                        >
                            <i className="fa fa-times" />
                        </button>
                        <InfoTooltip type="danger" target={`delete-member-btn-${record.id}`}>
                            Delete Member
                        </InfoTooltip>
                    </div>
                </div>
            ),
        },
    ];

    return (
        <div className="w-full mb-6 sm:px-3 md:w-7/12 xl:w-8/12">
            <div className="talent-dashboard-panel">

                <div className="talent-panel-content-container">
                    <div className="talent-panel-section">

                        <div className="w-full overflow-auto">

                            <div className="flex flex-row items-center justify-between">

                                <p className="text-lg font-semibold">
                                    Members {!isLoading && members?.length > 0 && (<span>({members?.length})</span>)}
                                </p>
                                {!isLoading && !error && members?.length > 0 && (
                                    <Link to="/supplier/manage-profile/talent-resources" className="btn btn-primary">
                                        Manage Members
                                    </Link>
                                )}
                            </div>

                            <div className="flex-grow py-3 flex flex-col items-stretch justify-center self-stretch">
                                {isLoading && (
                                    <div className="flex-1 w-full py-3 flex flex-col items-center justify-center">
                                        <LoadingSpinner />
                                    </div>
                                )}
                                {!isLoading && error && (
                                    <div className="self-center">
                                        <Empty
                                            image={(
                                                <i className="fa fa-exclamation-circle fa-3x text-gray-400" />
                                            )}
                                            imageStyle={{ height: 'auto' }}
                                            description={
                                                <span>
                                                    Something went wrong
                                                </span>
                                            }
                                        >
                                            <button className="mx-auto min-w-[80px] btn btn-dark" onClick={refetch}>Retry</button>
                                        </Empty>
                                    </div>
                                )}
                                {!isLoading && !error && (members.length > 0 ? (
                                    <Table className="flex-1 supplier-members-table" columns={columns} showHeader={false}
                                           dataSource={members}
                                           pagination={{ defaultPageSize: 4, size: 'small' }} />
                                ) : (
                                    <Empty description="No members invited yet" image={Empty.PRESENTED_IMAGE_SIMPLE}>

                                        <button className="mx-auto btn btn-primary">
                                            Invite Member
                                        </button>
                                    </Empty>
                                ))}
                            </div>

                        </div>
                    </div>
                </div>
            </div>

        </div>
    )
}

export default MembersPanel;

MembersPanel.propTypes = {};